// src/pages/BrandAwareness.js
import React from 'react';
import FeaturePageLayout from '../components/FeaturePageLayout';

// Import Icons and Images
import { 
    IoMegaphoneOutline,
    IoGlobeOutline,
    IoSyncOutline,
    IoHeartCircleOutline,
    IoEyeOutline
} from 'react-icons/io5';
import image1 from '../assets/images/Generate.png';
import image2 from '../assets/images/ConnectSocial.png';
import image3 from '../assets/images/Messages.png';
import image4 from '../assets/images/Analytics.png';
import heroBg from '../assets/images/brand-awareness-background.png'; 

// --- Structured Data Schemas ---
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://oolook.in/" }, { "@type": "ListItem", "position": 2, "name": "Brand Awareness", "item": "https://oolook.in/brand-awareness" }]
};

const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Brand Management Software",
    "provider": { "@type": "Organization", "name": "Oolook" },
    "name": "AI-Powered Brand Awareness Platform",
    "description": "Build a memorable brand with Oolook. Our AI tools help you create consistent content, expand your reach across all platforms, and build a loyal community.",
    "url": "https://oolook.in/brand-awareness"
};

// --- Page-Specific Data ---
const brandAwarenessPageData = {
  seo: {
    title: "Boost Brand Awareness with AI Content | Oolook",
    description: "Build a brand that stands out. Oolook's AI platform helps you create consistent on-brand content, expand your reach, and build a memorable presence on social media.",
    image: 'https://oolook.in/og/brand-awareness.png',
    schemas: [breadcrumbSchema, serviceSchema]
  },
  hero: {
    icon: IoMegaphoneOutline,
    title: "Build a Brand They'll Remember",
    subtitle: "Go from just another account to a go-to brand in your niche. Oolook helps you craft a consistent, memorable presence that builds trust and captivates your audience.",
    backgroundImage: heroBg
  },
  featureSection: {
    title: "The Blueprint for a Stronger Brand",
    subtitle: "Our tools are designed to ensure every post, message, and campaign reinforces your unique brand identity.",
    features: [
      { title: "AI That Learns Your Voice", description: "Our AI generates content that is always on-brand, capturing your unique tone, style, and messaging.", image: image1 },
      { title: "Consistent Cross-Platform Presence", description: "Publish cohesive campaigns across all your social channels to create a unified and professional brand experience.", image: image2 },
      { title: "Build an Engaged Community", description: "Foster a loyal following by consistently engaging with your audience and participating in relevant conversations.", image: image3 },
      { title: "Track Your Brand's Reach", description: "Monitor metrics like impressions, reach, and share of voice to understand how your brand awareness is growing over time.", image: image4 }
    ]
  },
  benefitGrid: {
    title: "The Results of a Memorable Brand",
    benefits: [
      { icon: IoGlobeOutline, title: "Expand Your Reach", description: "Reach new audiences and markets with a consistent and scalable content strategy." },
      { icon: IoSyncOutline, title: "Ensure Brand Consistency", description: "Guarantee that every piece of content perfectly reflects your brand's voice and values." },
      { icon: IoHeartCircleOutline, title: "Cultivate Trust & Loyalty", description: "A consistent, authentic brand presence builds the trust that turns followers into lifelong advocates." },
      { icon: IoEyeOutline, title: "Become Top-of-Mind", description: "Be the first brand your audience thinks of when they need a solution in your industry." }
    ]
  },
  stepByStep: {
    title: "Crafting Your Brand Identity with AI",
    subtitle: "Oolook makes it easy to define and scale your brand's presence across social media.",
    steps: [
      { title: "1. Define Your Brand Voice", description: "Provide Oolook with examples of your content. Our AI will analyze your tone, style, and key messaging to create a unique brand profile." },
      { title: "2. Generate On-Brand Content", description: "Use your brand profile to generate a stream of content ideas, posts, and campaigns that are always perfectly aligned with your identity." },
      { title: "3. Schedule for Consistency", description: "Use our visual calendar to ensure you have a steady, consistent presence on all your most important channels." },
      { title: "4. Monitor Your Presence", description: "Track your brand's growth with our analytics, monitoring key awareness metrics like reach, impressions, and mentions." }
    ]
  },
  targetAudience: {
    title: "An Essential Platform for Brand Builders"
  }
};

const BrandAwareness = () => {
  return (
    <FeaturePageLayout pageData={brandAwarenessPageData} />
  );
};

export default BrandAwareness;